import React, { useState } from "react";
import { Ticket } from "../../../../../models/Ticket";
import EAInfoTile from "../../../../shared/InfoTile/EAInfoTile";
import { differenceInDays, format } from "date-fns";
import PassTag from "./PassTag";
import "./SinglePass.scss";
import useDesktopSize from "../../../../../../utils/hooks/useDesktopSize";
import {
  getDateLabel,
  getEntranceInfo,
  isTicketInactive,
  getInfoTileIconType,
  getActionButtonLabel,
  getActionButtonVariant,
  ICON_COMPONENTS,
  isTicketType,
  isTicketExpired,
  getInfoTileColorForExpired,
  isPassType,
  PassType,
  hasEntranceWarning,
} from "../../../../../utils/TicketPassHelper";
import TicketDetailsModal from "../PassModal/TicketDetailsModal";
import PassDetailsModal from "../PassModal/PassDetailsModal";
import EAButton from "../../../../shared/Buttons/EAButton";
import EAIconButton from "../../../../shared/Buttons/EAIconButton";
import { MdChevronRight } from "react-icons/md";

interface IProps {
  pass: Ticket;
  onRefreshTickets?: () => void;
}

const SinglePass: React.FC<IProps> = ({ pass, onRefreshTickets }) => {
  const [openDetails, setOpenDetails] = useState(false);
  const isDesktop = useDesktopSize();

  // Sprawdzenie czy bilet jest wygasły
  const isExpired = isTicketExpired(pass);

  // Sprawdzenie czy bilet jest nieaktywny (brak wybranego terminu)
  // LIMITED bilety nigdy nie będą nieaktywne - to jest obsługiwane w isTicketInactive!
  const isInactive = !isExpired && isTicketInactive(pass);

  // Sprawdź czy to jest karnet zajęciowy (passtype 2, ticket false)
  const isClassPassCard = isPassType(pass) && pass.passtype === PassType.CLASS_PASS_SINGLE;

  // Sprawdzenie czy kończą się wejścia
  const hasLowEntrances = !isExpired && hasEntranceWarning(pass);

  // Dla karnetu zajęciowego bez terminu używamy ostrzeżenia (żółty), nie błędu (czerwony)
  const showAsError = isInactive && !isClassPassCard;
  const showAsWarning = isInactive && isClassPassCard;

  // Sprawdzenie czy bilet wkrótce traci ważność (7 dni lub mniej)
  const getDaysUntilExpiration = () => {
    const validToDate = typeof pass.validto === "number" ? new Date(pass.validto) : new Date(pass.validto);
    return differenceInDays(validToDate, new Date());
  };

  const daysUntilExpiration = getDaysUntilExpiration();
  const isExpiringsoon = !isExpired && daysUntilExpiration >= 0 && daysUntilExpiration <= 7;

  const IconComponent = ICON_COMPONENTS[getInfoTileIconType(pass)];
  const infoTileColor = getInfoTileColorForExpired(pass, isExpired);
  const buttonLabel = getActionButtonLabel(pass);
  const buttonVariant = getActionButtonVariant(pass);

  const handleActionClick = () => {
    setOpenDetails(true);
  };

  const handleCloseModal = () => {
    setOpenDetails(false);
    onRefreshTickets?.();
  };

  return (
    <>
      <div
        className={`single-pass__container ${showAsError ? "single-pass__container--inactive" : ""} ${
          (isExpiringsoon || showAsWarning || hasLowEntrances) && !showAsError
            ? "single-pass__container--expiring-soon"
            : ""
        } ${isExpired ? "single-pass__container--expired" : ""} ${
          !isDesktop ? "single-pass__container--mobile-clickable" : ""
        }`}
        onClick={!isDesktop ? handleActionClick : undefined}
      >
        <div className="single-pass__left-col">
          {isDesktop && <EAInfoTile icon={<IconComponent />} color={infoTileColor} />}
          <div className="single-pass__left-col-content">
            <PassTag pass={pass} isInactive={isInactive} isExpiringsoon={isExpiringsoon} isExpired={isExpired} />
            <h5>{pass.name}</h5>
            <span className="body-text-small text-color-dark-gray">
              {getDateLabel(pass)} {format(new Date(pass.validto), "dd.MM.yyyy")} • {getEntranceInfo(pass)}
            </span>
          </div>
        </div>

        <div className="single-pass__right-col">
          {isDesktop ? (
            <EAButton title={buttonLabel} variant={buttonVariant} onClick={handleActionClick} />
          ) : (
            <EAIconButton
              title={buttonLabel}
              icon={<MdChevronRight />}
              variant="icon"
              color="primary"
              onClick={handleActionClick}
            />
          )}
        </div>
      </div>

      {isTicketType(pass) ? (
        <TicketDetailsModal
          isOpen={openDetails}
          onClose={handleCloseModal}
          ticket={pass}
          onRefreshTickets={onRefreshTickets}
        />
      ) : (
        <PassDetailsModal
          isOpen={openDetails}
          onClose={handleCloseModal}
          pass={pass}
          onRefreshTickets={onRefreshTickets}
        />
      )}
    </>
  );
};

export default SinglePass;
